import { useState, useEffect, useMemo, useCallback } from "react";

export const usePagination = (
  data,
  initialItemsPerPage = 10,
  totalItems = 0
) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  const totalPages = useMemo(() => {
    const total = totalItems || data.length;
    return Math.ceil(total / itemsPerPage) || 1;
  }, [data.length, totalItems, itemsPerPage]);

  const currentData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, itemsPerPage]);

  const goToPage = useCallback(
    (page) => {
      const validPage = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(validPage);
      return validPage;
    },
    [totalPages]
  );

  const nextPage = useCallback(() => {
    if (currentPage < totalPages) {
      const nextPageNumber = currentPage + 1;
      setCurrentPage(nextPageNumber);
      return nextPageNumber;
    }
    return currentPage;
  }, [currentPage, totalPages]);

  const prevPage = useCallback(() => {
    if (currentPage > 1) {
      const prevPageNumber = currentPage - 1;
      setCurrentPage(prevPageNumber);
      return prevPageNumber;
    }
    return currentPage;
  }, [currentPage]);

  const changeItemsPerPage = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (currentPage > 1 && data.length <= itemsPerPage) {
      setCurrentPage(1);
    } else if (
      currentPage > Math.ceil(data.length / itemsPerPage) &&
      data.length > 0
    ) {
      setCurrentPage(Math.ceil(data.length / itemsPerPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  return {
    currentPage,
    setCurrentPage,
    totalPages,
    prevPage,
    nextPage,
    goToPage,
    currentData,
    perPage: itemsPerPage,
    changeItemsPerPage,
  };
};
